import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { Layout, Avatar, Popover, Space, Badge, Button, Tag, Modal } from 'antd';
import { LogoutOutlined, BellOutlined, CaretDownOutlined, ExclamationCircleOutlined, RollbackOutlined, SafetyCertificateOutlined } from '@ant-design/icons';
import type { MenuProps } from 'antd';
import { UserInfo, GroupInfo, StringMapString } from '@/models/perms_type';
import styles from './index.module.less';
import { UserInfoIcon } from '@/icons/UserInfo';
import { store as starkStore } from '@ice/stark-data';

import momenta from '@/assets/momenta.png'
import mmtLogo from '@/assets/mmt-logo.png';
import xdLogo from '@/assets/xd.png';
import ytLogo from '@/assets/yt.png';

const logoSources = {
  mmt: mmtLogo,
  xd: xdLogo,
  yt: ytLogo,
};

type MenuItem = Required<MenuProps>['items'][number];
const { Header: AntHeader } = Layout;

interface HeaderProps {
  pathname: string;
  handleJumpTo: (path: string) => void;
  handleLogout: () => void;
  userInfo: UserInfo;
  activeMenu: string;
  isSystemAdmin: boolean;
  isGroupAdmin: boolean;
  handleActiveMenu: (activeMenu: string) => void;
}

const Header: React.FC<HeaderProps> = ({
  pathname,
  handleJumpTo,
  handleLogout,
  userInfo,
  activeMenu,
  handleActiveMenu,
  isGroupAdmin,
  isSystemAdmin,
}) => {
  const [selectedKeys, setSelectedKeys] = useState<string>('');
  const [messageCount, setMessageCount] = useState<number>(8); //TODO: 1114版本没有
  const [popoverVisible, setPopoverVisible] = useState(false);
  const [popoverWidth, setPopoverWidth] = useState<number>();
  const headerLeftRef = useRef<HTMLDivElement>(null);
  const [currentTenant, setCurrentTenant] = useState<GroupInfo>({});
  const [productLines, setProductLines] = useState<StringMapString[]>([]);


  // 产品目录
  useEffect(() => {
    // 优先从 localStorage 恢复上次选择的租户
    const storedGroupId = localStorage.getItem('selectedGroupId');
    const groupMapEntries = Object.entries(userInfo.groupMap || {});
    let nextTenant = {} as GroupInfo;

    if (groupMapEntries.length > 0) {
      // 如果有缓存的 groupId，尝试找到对应的租户
      if (storedGroupId) {
        const matchedEntry = groupMapEntries.find(
          ([key, tenant]) => key === storedGroupId || tenant.groupId === storedGroupId
        );
        if (matchedEntry) {
          nextTenant = matchedEntry[1] as GroupInfo;
        } else {
          // 找不到缓存对应的租户时，使用第一个作为默认
          nextTenant = groupMapEntries[0][1] as GroupInfo;
        }
      } else {
        // 没有缓存则使用第一个租户作为默认
        nextTenant = groupMapEntries[0][1] as GroupInfo;
      }

      setCurrentTenant(nextTenant);
      starkStore.set('currentGroup', nextTenant?.groupId as string);
      // 保存到 localStorage 以便页面刷新后恢复
      if (nextTenant?.groupId) {
        localStorage.setItem('selectedGroupId', nextTenant.groupId);
      }
    }
  }, [userInfo])

  // 监听租户切换，更新产品线
  useEffect(() => {
    if (currentTenant?.groupId) {
      const productList: StringMapString[] = [];
      const groupMapProduct = userInfo?.groupMapProduct ?? {};
      const tenantProductList = groupMapProduct[currentTenant.groupId];

      if (tenantProductList?.includes('taxi')) {
        productList.push({ "key": '/taxi', "label": 'Robotaxi', "value": "taxi" });
      }
      if (tenantProductList?.includes('dlv')) {
        productList.push({ "key": '/dlv', "label": 'RoboDelivery', "value": "dlv" });
      }
      if (tenantProductList?.includes('truck')) {
        productList.push({ "key": '/truck', "label": 'RoboTruck', "value": "truck" });
      }

      setProductLines(productList);
    }
  }, [currentTenant, userInfo.groupMapProduct]);

  
  useEffect(() => {
    const el = headerLeftRef.current;
    if (!el) return;

    const measureWidth = () => {
      // 使用布局口径读取尺寸，避免 offsetWidth 在某些布局/渲染时序下读到不稳定值
      const width = el.getBoundingClientRect().width;
      setPopoverWidth(width - 20);
    };

    // 首次先测一次
    measureWidth();

    // 监听元素自身尺寸变化（图片/字体加载、flex 重新分配空间等都会触发）
    let ro: ResizeObserver | undefined;
    if (typeof ResizeObserver !== 'undefined') {
      ro = new ResizeObserver(() => {
        measureWidth();
      });
      ro.observe(el);
    }

    // 兜底：窗口 resize 时也测一次
    window.addEventListener('resize', measureWidth);
    return () => {
      window.removeEventListener('resize', measureWidth);
      ro?.disconnect();
    };
  }, []);


  useEffect(() => {
    if (productLines.length > 0 && activeMenu !== 'perms') {
      const currentPath = pathname;
      let activeItem: StringMapString | undefined;
      if (currentPath !== "/") {
        const activeMenuItem = productLines.find(
          (item: any) => currentPath.startsWith(item?.key)
        );
        if (activeMenuItem) {
          activeItem = activeMenuItem;
        }
      } else {
        activeItem = productLines[0];
      }
      if (activeItem) {
        setSelectedKeys(activeItem.key);
        handleActiveMenu(activeItem?.value as string);
        starkStore.set('currentProduct', activeItem?.value as string);
      }
    }
  }, [productLines, pathname, activeMenu]);

  useEffect(() => {
    starkStore.set('currentGroup', currentTenant?.groupId as string);
  }, [currentTenant]);

  const handleMenuClick = (item: StringMapString, shouldJump = false) => {
    setSelectedKeys(item.key);
    handleActiveMenu(item?.value as string);
    starkStore.set('currentProduct', item?.value as string);
    if (shouldJump) {
      handleJumpTo(item.key);
    }
  };

  const handlePermsClick = () => {
    const title = activeMenu === 'perms' ? '返回系统' : '权限中心';
    const content = activeMenu === 'perms' ? '确定返回系统吗' : '确定进入权限中心吗';
    Modal.confirm({
      title,
      icon: <ExclamationCircleOutlined />,
      content,
      okText: '确定',
      cancelText: '取消',
      onOk() {
        if (activeMenu === 'perms') {
          window.location.href = '/';
        } else {
          // 从 pagePaths 中找到第一个有权限的 /perms/ 页面
          const permsPaths = userInfo.pagePaths.filter(path => path.startsWith('/perms/'));
          const targetPath = permsPaths.length > 0 ? permsPaths[0] : '/perms';

          // 检查是否从子应用页面跳转
          const isFromChildApp = pathname.startsWith('/taxi') || pathname.startsWith('/dlv') || pathname.startsWith('/truck');

          if (isFromChildApp) {
            // 从子应用跳转到权限中心，使用完整刷新确保子应用被正确卸载和样式被清理
            window.location.href = targetPath;
          } else {
            // 主应用内部跳转，使用 SPA 方式
            handleJumpTo(targetPath);
          }
        }
      },
      centered: true,
    });
  };

  const handleTenantSwitch = (tenant) => {
    setCurrentTenant(tenant);
    // 保存到 localStorage 以便刷新后恢复
    if (tenant?.groupId) {
      localStorage.setItem('selectedGroupId', tenant.groupId);
    }
    setPopoverVisible(false);
  };

  const showLogoutConfirm = () => {
    Modal.confirm({
      title: '提示',
      icon: <ExclamationCircleOutlined />,
      content: '确定退出该账号吗',
      okText: '确定',
      cancelText: '取消',
      onOk() {
        handleLogout();
      },
      centered: true,
    });
  };

  // 获取用户显示名称
  const getDisplayName = () => {
    if (userInfo.account) {
      return userInfo.account;
    }
    return '用户';
  };


  const popoverContent = useMemo(() => {
    const roleKeys = Object.keys(userInfo.roleMap || {});
    const groupIds = new Set<string>();

    roleKeys.forEach(key => {
      if (key.startsWith('dr:group:')) {
        const suffix = key.replace('dr:group:', '');

        if (suffix.includes('Member')) {
          const parts = suffix.split(/[A-Z]/);
          if (parts.length > 0 && parts[0]) {
            groupIds.add(parts[0]);
          }
        }
      }
    });


    const filteredGroups = Object.entries(userInfo.groupMap || {})
      .filter(([key, tenant]) => groupIds.has(key) || (tenant.groupId && groupIds.has(tenant.groupId)));

    return (
      <div className={styles.popoverContent}>
        {filteredGroups.map(([key, tenant]) => (
          <div className={styles.popoverItem} key={key} onClick={() => handleTenantSwitch(tenant)}>
            <div className={styles.logo}>
              <div className={styles.logoLeft}>
                <div className={styles.logoImageWrapperSmall}>
                  <img src={tenant.groupLogo} alt={tenant.groupName} className={styles.logoImage} />
                </div>
                <div className={styles.tenantName}>{tenant.groupName}</div>
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  }, [userInfo.groupMap, userInfo.roleMap]);

  const userDropdownMenu = useMemo(() => (
    <div className={styles.userDropdown}>
      {/* 用户信息 */}
      <div className={styles.dropdownUserInfo}>
        {/* 头像和名字以及角色 */}
        <div className={styles.info}>
          {/* 头像 */}
          <div className={styles.avatar}>
            <Avatar size={48} style={{ backgroundColor: '#D0E5FF' }}>
              {getDisplayName().slice(-2)}
            </Avatar>
          </div>
          {/* 名字和角色卡片 */}
          <div className={styles.nameAndCards}>
            <div className={styles.name}>{userInfo.account}</div>
            <div className={styles.roleCards}>
              {Object.entries(userInfo.roleMap || {}).filter(([key, value]) => key.startsWith("sr")).map(([key, value]) => (
                <Tag key={key} color="blue">{value}</Tag>
              ))}
            </div>

          </div>
        </div>
        <span className={styles.xdivider}></span>
        {/* 手机号 */}
        <div className={styles.mobile}>
          <div>手机号</div>
          <div>{userInfo.mobile} </div>
        </div>
        <span className={styles.xdivider}></span>
        {/* 邮箱 */}
        <div className={styles.email}>
          <div>邮箱</div>
          <div>{userInfo.email}</div>
        </div>
      </div>
      <span className={styles.xdivider}></span>
      <div className={styles.btns}>
        {(isGroupAdmin || isSystemAdmin)
          &&
          (<Button
            type="primary"
            onClick={handlePermsClick}
            className={styles.logoutButton}
            icon={activeMenu === 'perms' ? <RollbackOutlined /> : <SafetyCertificateOutlined />}
          >
            {activeMenu === 'perms' ? '返回系统' : '权限中心'}
          </Button>)
        }

        <Button
          icon={<LogoutOutlined />}
          onClick={showLogoutConfirm}
          className={styles.logoutButton}
        >
          退出登录
        </Button>
      </div>
    </div>
  ), [userInfo, handleLogout, activeMenu, productLines, handleJumpTo, isGroupAdmin, isSystemAdmin]);

  const canSwitchTenants = useMemo(() => {
    return userInfo.groupMap && Object.keys(userInfo.groupMap).length > 0;
  }, [userInfo.groupMap]);

  return (
    <AntHeader className={styles.header}>
      {/* 能否切换租户 */}
      {activeMenu === 'perms' ? (
        <div className={styles.headerLeft} ref={headerLeftRef}>
          {/* 这里不要生效样式 */}
          <div className={styles.awrapper}>
            <div className={styles.logo}>
              <div className={styles.logoLeft}>
                {/* <div className={styles.logoImageWrapperLarge}>
                  <img src={logoSources.mmt} alt="Momenta" className={styles.logoImage} />
                </div> */}
                <div className={styles.momentalogo}>
                  <img height={30} src={momenta} alt="" />
                </div>
              </div>
            </div>
          </div>
        </div>
      ) : canSwitchTenants ? (
        <Popover
          content={popoverContent}
          placement="bottom"
          trigger="click"
          arrow={false}
          open={popoverVisible}
          onOpenChange={setPopoverVisible}
          styles={{
            body: {
              width: popoverWidth
            }
          }}

        >
          <div className={styles.headerLeft} ref={headerLeftRef}>
            <div className={styles.wrapper}>
              <div className={styles.logo}>
                <div className={styles.logoLeft}>
                  {/* <div className={styles.logoImageWrapperLarge}>
                    <img src={logoSources.mmt} alt="Momenta" className={styles.logoImage} />
                  </div> */}
                  <div className={styles.momentalogo}>
                    <img height={30} src={momenta} alt="" />
                  </div>
                </div>
                <span className={styles.ydivider}></span>
                <div className={styles.logoRight}>
                  <div className={styles.logoImageWrapperLarge}>
                    <img src={currentTenant.groupLogo} alt={currentTenant.groupName} className={styles.logoImage} />
                  </div>
                  <div>{currentTenant.groupName}</div>
                </div>
              </div>
              <CaretDownOutlined style={{ marginLeft: 3, transition: 'transform 0.3s', transform: `rotate(${popoverVisible ? 180 : 0}deg)` }} width={15} height={15} />
            </div>
          </div>
        </Popover>
      ) : (
        <div className={styles.headerLeft} ref={headerLeftRef}>
          <div className={styles.wrapper}>
            <div className={styles.logo}>
              <div className={styles.logoLeft}>
                <div className={styles.momentalogo}>
                  <img height={30} src={momenta} alt="" />
                </div>
              </div>
              <span className={styles.ydivider}></span>
              <div className={styles.logoRight}>
                <div className={styles.logoImageWrapperLarge}>
                  <img src={currentTenant.groupLogo} alt={currentTenant.groupName} className={styles.logoImage} />
                </div>
                <div>{currentTenant.groupName}</div>
              </div>
            </div>
            {/* <CaretDownOutlined style={{ marginLeft: 8, transition: 'transform 0.3s', transform: `rotate(${popoverVisible ? 180 : 0}deg)`, display: 'none' }} /> */}
          </div>
        </div>
      )}

      {productLines.length > 0 && (
        <div className={styles.headerMiddle}>
          <div className={styles.wrapper}>
            {activeMenu !== 'perms' && productLines.map((menu: any) => {
              return <div
                key={menu.key}
                className={`${styles.menuItem} ${selectedKeys === menu.key ? styles.selected : ''}`}
                onClick={() => handleMenuClick(menu, true)}
              >
                {menu.label}
              </div>
            })}
          </div>
        </div>
      )}
      <div className={styles.headerRight}>
        <div className={styles.wrapper}>
          <Badge count={messageCount > 99 ? '99+' : messageCount} style={{ display: 'none' }}>
            <div className={styles.bell} style={{ display: 'none' }}>
              <BellOutlined style={{ fontSize: '20px', padding: '6px' }} />
            </div>
          </Badge>
          <Popover
            content={userDropdownMenu}
            placement="bottomRight"
            trigger="click"
            arrow={false}
          >
            <div className={styles.userInfo}>
              <Avatar icon={<UserInfoIcon />} size={30} style={{ backgroundColor: ' #D0E5FF' }} />
              <div className={styles.userName}>{getDisplayName()}</div>
            </div>
          </Popover>
        </div>
      </div>
    </AntHeader>
  );
};

export default Header;
